import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { WordReveal } from '@/components/ui/word-reveal';
import { ParallaxElement } from '@/components/ui/parallax';

export function Hero() {
  return (
    <section className="relative bg-gradient-to-b from-[#f4fbf7] via-white to-white pt-8 pb-12 md:pt-14 md:pb-20 overflow-hidden border-b border-gray-100">
      {/* Subtle ambient lighting */}
      <div className="pointer-events-none absolute -top-40 -left-40 h-96 w-96 rounded-full bg-emerald-400/10 blur-[120px]" />
      <div className="pointer-events-none absolute top-1/2 -right-40 h-[500px] w-[500px] rounded-full bg-emerald-600/10 blur-[150px]" />

      <div className="relative z-10 mx-auto w-full max-w-7xl 2xl:max-w-[1400px] px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-12 lg:gap-8">
          {/* LEFT COLUMN CONTENT */}
          <div className="lg:col-span-6 space-y-7">
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              className="inline-flex items-center gap-2 rounded-full bg-emerald-100/70 px-3.5 py-1.5 border border-emerald-200/80 shadow-2xs backdrop-blur-sm"
            >
              <span className="flex h-2 w-2 rounded-full bg-[#15803d] animate-pulse" />
              <span className="text-[11px] font-extrabold uppercase tracking-widest text-[#15803d]">
                Legal &amp; Business Compliance, Simplified
              </span>
            </motion.div>

            {/* Editorial Word-by-Word Scroll Reveal Heading */}
            <WordReveal
              as="h1"
              text="Your Compliance Partner, Always"
              highlightWords={['Always']}
              highlightClassName="text-[#15803d] relative inline-block font-black"
              className="font-display text-4xl font-extrabold tracking-tight text-[#0a1629] sm:text-5xl lg:text-[3.6rem] 2xl:text-[4.2rem] lg:leading-[1.1]"
              stagger={0.06}
            />

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.25, ease: [0.16, 1, 0.3, 1] }}
              className="max-w-xl 2xl:max-w-2xl text-base sm:text-lg 2xl:text-xl leading-relaxed text-gray-600 font-normal"
            >
              From GST registration to company incorporation — a verified Associate handles the
              paperwork end-to-end, in one chat. You upload once. We do the rest.
            </motion.p>

            {/* ACTION BUTTONS */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.35, ease: [0.16, 1, 0.3, 1] }}
              className="flex flex-wrap items-center gap-4 pt-2"
            >
              <Link to="/login">
                <button
                  type="button"
                  className="group relative inline-flex items-center gap-2.5 rounded-xl bg-[#15803d] hover:bg-[#166534] px-7 py-4 text-sm font-bold text-white shadow-lg shadow-emerald-900/20 transition-all duration-300 hover:shadow-emerald-900/30 hover:-translate-y-0.5 active:translate-y-0 cursor-pointer overflow-hidden"
                >
                  <span className="relative z-10">Client login</span>
                  <span className="relative z-10 transition-transform duration-300 group-hover:translate-x-1">
                    →
                  </span>
                  <div className="absolute inset-0 bg-gradient-to-r from-emerald-600 to-emerald-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </button>
              </Link>

              <a
                href="#how"
                onClick={(e) => {
                  e.preventDefault();
                  const el = document.getElementById('how');
                  if (el) {
                    el.scrollIntoView({ behavior: 'smooth' });
                    window.history.pushState(null, '', '#how');
                  }
                }}
              >
                <button
                  type="button"
                  className="inline-flex items-center gap-3 rounded-xl border border-gray-300 bg-white/90 backdrop-blur-md hover:bg-gray-50 px-6 py-4 text-sm font-bold text-gray-900 shadow-2xs hover:shadow-md transition-all duration-300 hover:-translate-y-0.5 active:translate-y-0 cursor-pointer"
                >
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-gray-900 text-white text-[9px] pl-0.5 shadow-xs">
                    ▶
                  </span>
                  <span>See how it works</span>
                </button>
              </a>
            </motion.div>
          </div>

          {/* RIGHT COLUMN WITH CLEAN DESK ILLUSTRATION */}
          <div className="lg:col-span-6 relative flex items-center justify-center lg:justify-end">
            <ParallaxElement speed={-0.1} className="w-full max-w-xl 2xl:max-w-2xl">
              <div className="relative group">
                <div className="absolute -inset-2 rounded-3xl bg-gradient-to-r from-emerald-500/20 via-teal-500/10 to-emerald-600/20 blur-2xl opacity-75 group-hover:opacity-100 transition-opacity duration-700" />
                <img
                  src="/hero_desk_clean.png"
                  alt="ECREGI Compliance Laptop and Desk Scene"
                  className="relative z-10 w-full max-w-full h-auto object-contain max-h-[460px] lg:max-h-[520px] 2xl:max-h-[620px] select-none drop-shadow-xl"
                />
              </div>
            </ParallaxElement>
          </div>
        </div>
      </div>
    </section>
  );
}
